import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { apiFetch } from "@/lib/api";

import { CroForecastFilterBar } from "../components/FilterBar";
import { CroForecastSubNav } from "../components/SubNav";
import { fmtCents } from "../format";
import { resolveSearch, type CroForecastSearch } from "../search";

interface BudgetRow {
  id: string;
  scope: string;
  scope_key: string;
  metric: string;
  cadence: string;
  fiscal_year: number;
  fiscal_period: number | null;
  value_cents: number;
}

function useBudget(year: number) {
  return useQuery({
    queryKey: ["cro_forecast", "budget", year],
    queryFn: () => apiFetch<BudgetRow[]>(`/cro_forecast/budget?year=${year}`),
  });
}

function useGoals(year: number) {
  return useQuery({
    queryKey: ["cro_forecast", "goals", year],
    queryFn: () => apiFetch<BudgetRow[]>(`/cro_forecast/goals?year=${year}`),
  });
}

function useUpsert(endpoint: "budget" | "goals") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Omit<BudgetRow, "id">) =>
      apiFetch(`/cro_forecast/${endpoint}`, {
        method: "PUT",
        body: payload,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["cro_forecast", endpoint] });
      void qc.invalidateQueries({ queryKey: ["cro_forecast", "summary"] });
      toast.success(`${endpoint === "budget" ? "Budget" : "Goal"} saved`);
    },
    onError: (err) => toast.error(`Save failed: ${(err as Error).message}`),
  });
}

export function CroForecastAdminPage() {
  const search = useSearch({ from: "/cro-forecast/admin" }) as CroForecastSearch;
  const { year } = resolveSearch(search);
  const budget = useBudget(year);
  const goals = useGoals(year);

  return (
    <div className="flex flex-col">
      <CroForecastFilterBar search={search} />
      <CroForecastSubNav search={search} />
      <div className="flex flex-col gap-6 p-4 md:p-6">
        <header>
          <h2 className="text-lg font-semibold">Admin — FY {year}</h2>
          <p className="text-sm text-muted-foreground">
            Set the budget plan (annual / quarterly / monthly per tier or
            segment) and the secondary targets (BDR generated, pipeline-by-source,
            TOF goals). Changes flow straight into the Summary tab.
          </p>
        </header>

        <BudgetCard
          title="Budget Plan"
          year={year}
          rows={budget.data ?? []}
          isLoading={budget.isLoading}
          endpoint="budget"
        />
        <BudgetCard
          title="Goal Targets"
          year={year}
          rows={goals.data ?? []}
          isLoading={goals.isLoading}
          endpoint="goals"
        />
      </div>
    </div>
  );
}

function BudgetCard({
  title,
  year,
  rows,
  isLoading,
  endpoint,
}: {
  title: string;
  year: number;
  rows: BudgetRow[];
  isLoading: boolean;
  endpoint: "budget" | "goals";
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between gap-3 text-sm">
          <span>{title}</span>
          <NewRowForm year={year} endpoint={endpoint} />
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
        {!isLoading && rows.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No rows yet. Use the form above to add one.
          </p>
        )}
        {rows.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Scope</TableHead>
                <TableHead>Key</TableHead>
                <TableHead>Metric</TableHead>
                <TableHead>Cadence</TableHead>
                <TableHead>Period</TableHead>
                <TableHead className="text-right">Value</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <RowEditor key={r.id} row={r} endpoint={endpoint} />
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function RowEditor({
  row,
  endpoint,
}: {
  row: BudgetRow;
  endpoint: "budget" | "goals";
}) {
  const [value, setValue] = useState<number>(row.value_cents);
  const mutate = useUpsert(endpoint);
  const dirty = value !== row.value_cents;

  function save() {
    mutate.mutate({
      scope: row.scope,
      scope_key: row.scope_key,
      metric: row.metric,
      cadence: row.cadence,
      fiscal_year: row.fiscal_year,
      fiscal_period: row.fiscal_period,
      value_cents: value,
    });
  }

  return (
    <TableRow>
      <TableCell className="font-mono text-xs">{row.scope}</TableCell>
      <TableCell className="font-mono text-xs">{row.scope_key}</TableCell>
      <TableCell className="font-mono text-xs">{row.metric}</TableCell>
      <TableCell className="font-mono text-xs">{row.cadence}</TableCell>
      <TableCell className="font-mono text-xs">
        {row.fiscal_period ?? "—"}
      </TableCell>
      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-2">
          <Input
            type="number"
            value={Math.round(value / 100)}
            className="w-32 text-right font-mono"
            onChange={(e) => {
              const v = Number(e.target.value);
              if (!Number.isNaN(v)) setValue(Math.round(v * 100));
            }}
          />
          <span className="font-mono text-xs text-muted-foreground">
            ≈ {fmtCents(value)}
          </span>
        </div>
      </TableCell>
      <TableCell className="text-right">
        <Button
          size="sm"
          variant={dirty ? "default" : "ghost"}
          disabled={!dirty || mutate.isPending}
          onClick={save}
        >
          {mutate.isPending ? "Saving…" : "Save"}
        </Button>
      </TableCell>
    </TableRow>
  );
}

function NewRowForm({
  year,
  endpoint,
}: {
  year: number;
  endpoint: "budget" | "goals";
}) {
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState("team");
  const [scopeKey, setScopeKey] = useState("team");
  const [metric, setMetric] = useState(endpoint === "budget" ? "new_rev" : "bdr_generated");
  const [cadence, setCadence] = useState("quarterly");
  const [period, setPeriod] = useState<number | null>(1);
  const [dollars, setDollars] = useState<number>(0);
  const mutate = useUpsert(endpoint);

  if (!open) {
    return (
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        + New row
      </Button>
    );
  }

  function save() {
    mutate.mutate(
      {
        scope,
        scope_key: scopeKey,
        metric,
        cadence,
        fiscal_year: year,
        fiscal_period: cadence === "annual" ? null : period,
        value_cents: Math.round(dollars * 100),
      },
      {
        onSuccess: () => {
          setOpen(false);
          setDollars(0);
        },
      },
    );
  }

  return (
    <div className="flex flex-wrap items-end gap-2 rounded-md border bg-muted/30 p-2">
      <Field label="Scope">
        <Select value={scope} onValueChange={setScope}>
          <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
          <SelectContent>
            {["team", "tier", "rep", "segment"].map((s) => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <Field label="Key">
        <Input value={scopeKey} onChange={(e) => setScopeKey(e.target.value)} className="w-28" />
      </Field>
      <Field label="Metric">
        <Input value={metric} onChange={(e) => setMetric(e.target.value)} className="w-32" />
      </Field>
      <Field label="Cadence">
        <Select value={cadence} onValueChange={setCadence}>
          <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
          <SelectContent>
            {["annual", "quarterly", "monthly", "weekly"].map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <Field label="Period">
        <Input
          type="number"
          value={period ?? ""}
          onChange={(e) => setPeriod(e.target.value === "" ? null : Number(e.target.value))}
          className="w-16"
          placeholder="—"
        />
      </Field>
      <Field label="Value $">
        <Input
          type="number"
          value={dollars}
          onChange={(e) => setDollars(Number(e.target.value) || 0)}
          className="w-32"
        />
      </Field>
      <div className="flex gap-1">
        <Button size="sm" onClick={save} disabled={mutate.isPending}>
          {mutate.isPending ? "…" : "Save"}
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
        {label}
      </Label>
      {children}
    </div>
  );
}
